import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api, apiUrl, ApiError } from './client';
import { useAuthStore } from '@/hooks/useStore';
import { downloadBlob, readDownloadResponseBlob } from '../utils/native-download';
import {
  assertAuthenticationOwner,
  binaryResponseError,
  requireAuthenticationOwner,
  type AuthenticationOwner,
} from './binaryRequest';

// Credentials Vault API
export interface TeamCredential {
  id: string;
  name: string;
  url?: string;
  username?: string;
  password?: string;
  notes?: string;
  created_by: string;
  created_by_name?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CredentialFile {
  id: string;
  credential_id: string;
  filename: string;
  mime_type: string;
  file_size: number;
  uploader_id: string;
  uploader_name: string;
  created_at: string;
}

export interface VaultPasskey {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

interface VaultUnlockResponse {
  vault_grant: string;
  expires_in: number;
  expires_at: string;
}

// Grants intentionally live only in memory. Reloading, signing out, navigating
// away, or explicitly locking the vault requires another passkey verification.
let vaultGrant: string | null = null;
let vaultOwner: AuthenticationOwner | null = null;
let vaultGeneration = 0;

function lockVault(): void {
  vaultGeneration += 1;
  vaultGrant = null;
  vaultOwner = null;
}

function assertVaultRequest(owner: AuthenticationOwner, generation: number): void {
  assertAuthenticationOwner(owner);
  if (generation !== vaultGeneration || useAuthStore.getState().token !== owner.token) {
    throw new ApiError('Vault session changed. Unlock the vault again to continue.', 423);
  }
}

function vaultHeaders(): Record<string, string> {
  const auth = useAuthStore.getState();
  if (vaultOwner && (auth.user?.id !== vaultOwner.userId || auth.token !== vaultOwner.token)) {
    lockVault();
  }
  return vaultGrant ? { 'X-Vault-Grant': vaultGrant } : {};
}

async function withVaultGrant<T>(request: () => Promise<T>): Promise<T> {
  const owner = requireAuthenticationOwner();
  vaultHeaders();
  const generation = vaultGeneration;
  try {
    const result = await request();
    assertVaultRequest(owner, generation);
    return result;
  } catch (error) {
    if (error instanceof ApiError && error.status === 423 && generation === vaultGeneration)
      lockVault();
    throw error;
  }
}

async function listCredentials(): Promise<{ credentials: TeamCredential[] }> {
  return withVaultGrant(() =>
    api.get<{ credentials: TeamCredential[] }>('/credentials', undefined, {
      headers: vaultHeaders(),
    }),
  );
}

async function acceptPasskeyGrant(
  unlocked: VaultUnlockResponse,
  owner: AuthenticationOwner,
  generation: number,
) {
  assertVaultRequest(owner, generation);
  vaultGrant = unlocked.vault_grant;
  vaultOwner = owner;
  try {
    const result = await listCredentials();
    return { ...result, expires_in: unlocked.expires_in, expires_at: unlocked.expires_at };
  } catch (error) {
    if (generation === vaultGeneration) lockVault();
    throw error;
  }
}

export const credentialsApi = {
  getVaultStatus: async () => {
    const owner = requireAuthenticationOwner();
    const result = await api.get<{
      is_setup: boolean;
      can_reset_password: boolean;
      auth_method: 'password' | 'passkey';
      has_passkey: boolean;
      can_recover_passkey: boolean;
      configuration_error: string | null;
    }>('/credentials/vault/status');
    assertAuthenticationOwner(owner);
    return result;
  },

  unlockWithPasskey: async () => {
    lockVault();
    const generation = vaultGeneration;
    const owner = requireAuthenticationOwner();
    const ceremony = await api.post<{
      ceremony_id: string;
      options: PublicKeyCredentialRequestOptionsJSON;
    }>('/credentials/vault/passkeys/authentication/options', {});
    assertVaultRequest(owner, generation);
    const response = await startAuthentication({ optionsJSON: ceremony.options });
    assertVaultRequest(owner, generation);
    const unlocked = await api.post<VaultUnlockResponse>(
      '/credentials/vault/passkeys/authentication/verify',
      { ceremony_id: ceremony.ceremony_id, response },
    );
    return acceptPasskeyGrant(unlocked, owner, generation);
  },

  registerPasskey: async (
    data: { password?: string; enrollment_token?: string; name?: string } = {},
  ) => {
    const owner = requireAuthenticationOwner();
    const headers = vaultHeaders();
    const generation = vaultGeneration;
    const ceremony = await api.post<{
      ceremony_id: string;
      options: PublicKeyCredentialCreationOptionsJSON;
    }>('/credentials/vault/passkeys/registration/options', data, { headers });
    assertVaultRequest(owner, generation);
    const response = await startRegistration({ optionsJSON: ceremony.options });
    assertVaultRequest(owner, generation);
    const unlocked = await api.post<VaultUnlockResponse>(
      '/credentials/vault/passkeys/registration/verify',
      { ceremony_id: ceremony.ceremony_id, response, name: data.name },
      { headers },
    );
    return acceptPasskeyGrant(unlocked, owner, generation);
  },

  listPasskeys: () =>
    withVaultGrant(() =>
      api.get<{ passkeys: VaultPasskey[] }>('/credentials/vault/passkeys', undefined, {
        headers: vaultHeaders(),
      }),
    ),

  deletePasskey: (id: string) =>
    withVaultGrant(() =>
      api.delete<{ message: string }>(
        `/credentials/vault/passkeys/${encodeURIComponent(id)}`,
        undefined,
        { headers: vaultHeaders() },
      ),
    ),

  requestPasskeyRecovery: async () => {
    const owner = requireAuthenticationOwner();
    const result = await api.post<{ message: string }>(
      '/credentials/vault/passkeys/recovery/request',
      {},
    );
    assertAuthenticationOwner(owner);
    return result;
  },

  invitePasskeyAdministrator: (email: string) =>
    withVaultGrant(() =>
      api.post<{ message: string }>(
        '/credentials/vault/passkeys/invitations',
        { email },
        { headers: vaultHeaders() },
      ),
    ),

  lockVault,

  list: listCredentials,

  create: (data: {
    name: string;
    url?: string;
    username?: string;
    password?: string;
    notes?: string;
  }) =>
    withVaultGrant(() =>
      api.post<{ credential: TeamCredential }>('/credentials', data, { headers: vaultHeaders() }),
    ),

  update: (
    id: string,
    data: { name?: string; url?: string; username?: string; password?: string; notes?: string },
  ) =>
    withVaultGrant(() =>
      api.patch<{ message: string }>(`/credentials/${id}`, data, { headers: vaultHeaders() }),
    ),

  delete: (id: string) =>
    withVaultGrant(() =>
      api.delete<{ message: string }>(`/credentials/${id}`, undefined, { headers: vaultHeaders() }),
    ),

  listFiles: () =>
    withVaultGrant(() =>
      api.get<{ files: CredentialFile[] }>('/credentials/files', undefined, {
        headers: vaultHeaders(),
      }),
    ),

  uploadFile: async (credId: string, file: File): Promise<{ file: CredentialFile }> => {
    const owner = requireAuthenticationOwner();
    vaultHeaders();
    const generation = vaultGeneration;
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(apiUrl(`/credentials/${credId}/files`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${owner.token}`,
        'X-Requested-With': 'XMLHttpRequest',
        ...vaultHeaders(),
      },
      body: formData,
    });
    if (!response.ok) {
      if (response.status === 423 && generation === vaultGeneration) lockVault();
      throw await binaryResponseError(response, 'Upload failed');
    }
    const result = (await response.json()) as { file: CredentialFile };
    assertVaultRequest(owner, generation);
    return result;
  },

  downloadFile: async (fileId: string, filename: string): Promise<void> => {
    const owner = requireAuthenticationOwner();
    vaultHeaders();
    const generation = vaultGeneration;
    const response = await fetch(apiUrl(`/credentials/files/${fileId}`), {
      headers: {
        Authorization: `Bearer ${owner.token}`,
        'X-Requested-With': 'XMLHttpRequest',
        ...vaultHeaders(),
      },
    });
    if (!response.ok) {
      if (response.status === 423 && generation === vaultGeneration) lockVault();
      throw await binaryResponseError(response, 'Download failed');
    }
    const blob = await readDownloadResponseBlob(response);
    await downloadBlob(blob, filename, {
      assertCanDeliver: () => assertVaultRequest(owner, generation),
    });
  },

  deleteFile: (fileId: string) =>
    withVaultGrant(() =>
      api.delete<{ message: string }>(`/credentials/files/${fileId}`, undefined, {
        headers: vaultHeaders(),
      }),
    ),
};

// Admin Documents API
export interface AdminDocument {
  id: string;
  organization_id: string;
  uploaded_by: string;
  uploaded_by_name: string;
  name: string;
  category: string;
  description?: string;
  filename: string;
  mime_type: string;
  file_size: number;
  created_at: string;
  updated_at: string;
}

export const adminDocumentsApi = {
  list: () =>
    withVaultGrant(() =>
      api.get<{ documents: AdminDocument[] }>('/credentials/admin-documents', undefined, {
        headers: vaultHeaders(),
      }),
    ),

  upload: async (data: {
    file: File;
    name: string;
    category: string;
    description?: string;
  }): Promise<{ document: AdminDocument }> => {
    const owner = requireAuthenticationOwner();
    vaultHeaders();
    const generation = vaultGeneration;
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('name', data.name);
    formData.append('category', data.category);
    if (data.description) formData.append('description', data.description);
    const response = await fetch(apiUrl('/credentials/admin-documents'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${owner.token}`,
        'X-Requested-With': 'XMLHttpRequest',
        ...vaultHeaders(),
      },
      body: formData,
    });
    if (!response.ok) {
      if (response.status === 423 && generation === vaultGeneration) lockVault();
      throw await binaryResponseError(response, 'Upload failed');
    }
    const result = (await response.json()) as { document: AdminDocument };
    assertVaultRequest(owner, generation);
    return result;
  },

  download: async (docId: string, filename: string): Promise<void> => {
    const owner = requireAuthenticationOwner();
    vaultHeaders();
    const generation = vaultGeneration;
    const response = await fetch(apiUrl(`/credentials/admin-documents/${docId}`), {
      headers: {
        Authorization: `Bearer ${owner.token}`,
        'X-Requested-With': 'XMLHttpRequest',
        ...vaultHeaders(),
      },
    });
    if (!response.ok) {
      if (response.status === 423 && generation === vaultGeneration) lockVault();
      throw await binaryResponseError(response, 'Download failed');
    }
    const blob = await readDownloadResponseBlob(response);
    await downloadBlob(blob, filename, {
      assertCanDeliver: () => assertVaultRequest(owner, generation),
    });
  },

  update: (docId: string, data: { name?: string; category?: string; description?: string }) =>
    withVaultGrant(() =>
      api.patch<{ message: string }>(`/credentials/admin-documents/${docId}`, data, {
        headers: vaultHeaders(),
      }),
    ),

  delete: (docId: string) =>
    withVaultGrant(() =>
      api.delete<{ message: string }>(`/credentials/admin-documents/${docId}`, undefined, {
        headers: vaultHeaders(),
      }),
    ),
};
